import type { Session, User } from "@supabase/supabase-js";
import { useQueryClient } from "@tanstack/react-query";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { localSupabaseClient, type TypedSupabaseClient } from "../../shared/api/supabase";
import { PublicAppError } from "../../shared/lib/errors";

type SessionStatus = "loading" | "authenticated" | "anonymous";

interface SessionContextValue {
  status: SessionStatus;
  session: Session | null;
  user: User | null;
  recovery: boolean;
  signIn(email: string, password: string): Promise<void>;
  signUp(email: string, password: string): Promise<void>;
  requestPasswordReset(email: string): Promise<void>;
  updatePassword(password: string): Promise<void>;
  signOut(): Promise<void>;
}

const SessionContext = createContext<SessionContextValue | null>(null);

function authFailure(kind: "credentials" | "reset" | "signup" | "password" | "logout") {
  const messages = {
    credentials: "Não foi possível entrar. Confira suas credenciais.",
    reset: "Não foi possível enviar o link de recuperação.",
    signup: "Não foi possível criar a conta.",
    password: "Não foi possível atualizar a senha.",
    logout: "Não foi possível encerrar a sessão.",
  };
  return new PublicAppError(`auth_${kind}`, messages[kind]);
}

interface SessionProviderProps {
  children: ReactNode;
  client?: TypedSupabaseClient | null;
}

export function SessionProvider({ children, client = localSupabaseClient }: SessionProviderProps) {
  const queryClient = useQueryClient();
  const [session, setSession] = useState<Session | null>(null);
  const [status, setStatus] = useState<SessionStatus>("loading");
  const [recovery, setRecovery] = useState(false);

  if (client === null) {
    throw new PublicAppError(
      "missing_public_config",
      "A configuração pública de autenticação não está disponível.",
    );
  }

  useEffect(() => {
    let mounted = true;
    void client.auth.getSession().then(({ data }) => {
      if (!mounted) return;
      setSession(data.session);
      setStatus(data.session === null ? "anonymous" : "authenticated");
    });
    const { data } = client.auth.onAuthStateChange((event, nextSession) => {
      if (!mounted) return;
      if (event === "PASSWORD_RECOVERY") setRecovery(true);
      if (event === "SIGNED_OUT") {
        setRecovery(false);
        queryClient.clear();
      }
      setSession(nextSession);
      setStatus(nextSession === null ? "anonymous" : "authenticated");
    });
    return () => {
      mounted = false;
      data.subscription.unsubscribe();
    };
  }, [client, queryClient]);

  const signIn = useCallback(
    async (email: string, password: string) => {
      const { error } = await client.auth.signInWithPassword({ email, password });
      if (error !== null) throw authFailure("credentials");
    },
    [client],
  );
  const signUp = useCallback(
    async (email: string, password: string) => {
      const { error } = await client.auth.signUp({ email, password });
      if (error !== null) throw authFailure("signup");
    },
    [client],
  );
  const requestPasswordReset = useCallback(
    async (email: string) => {
      const redirectTo = `${window.location.origin}${window.location.pathname}`;
      const { error } = await client.auth.resetPasswordForEmail(email, { redirectTo });
      if (error !== null) throw authFailure("reset");
    },
    [client],
  );
  const updatePassword = useCallback(
    async (password: string) => {
      const { error } = await client.auth.updateUser({ password });
      if (error !== null) throw authFailure("password");
      setRecovery(false);
    },
    [client],
  );
  const signOut = useCallback(async () => {
    const { error } = await client.auth.signOut();
    if (error !== null) throw authFailure("logout");
  }, [client]);

  const value = useMemo<SessionContextValue>(
    () => ({
      status,
      session,
      user: session?.user ?? null,
      recovery,
      signIn,
      signUp,
      requestPasswordReset,
      updatePassword,
      signOut,
    }),
    [status, session, recovery, signIn, signUp, requestPasswordReset, updatePassword, signOut],
  );
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionContextValue {
  const value = useContext(SessionContext);
  if (value === null) throw new Error("SessionProvider ausente");
  return value;
}
